export interface ListeningProgress {
  positionSeconds: number;
  durationSeconds?: number;
  updatedAt: string;
}

const STORAGE_PREFIX = 'eruption.listening-progress';
const MAX_ENTRIES = 200;
const CLEANUP_THRESHOLD_SECONDS = 5;

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  return window.localStorage;
};

const buildKey = (showId: string): string => `${STORAGE_PREFIX}:${showId}`;

const parseProgress = (value: string | null): ListeningProgress | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as ListeningProgress;
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    if (typeof parsed.positionSeconds !== 'number') {
      return null;
    }

    return {
      positionSeconds: Math.max(0, parsed.positionSeconds),
      durationSeconds:
        typeof parsed.durationSeconds === 'number' && Number.isFinite(parsed.durationSeconds)
          ? Math.max(0, parsed.durationSeconds)
          : undefined,
      updatedAt: parsed.updatedAt ?? new Date().toISOString()
    };
  } catch {
    return null;
  }
};

const enforceStorageLimit = (storage: Storage) => {
  const keys = [];

  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      const progress = parseProgress(storage.getItem(key));
      if (progress) {
        keys.push({ key, updatedAt: new Date(progress.updatedAt).getTime() || 0 });
      } else {
        storage.removeItem(key);
      }
    }
  }

  if (keys.length <= MAX_ENTRIES) {
    return;
  }

  keys
    .sort((a, b) => a.updatedAt - b.updatedAt)
    .slice(0, keys.length - MAX_ENTRIES)
    .forEach(({ key }) => storage.removeItem(key));
};

export const listeningProgressService = {
  getProgress(showId: string): ListeningProgress | null {
    const storage = getStorage();
    if (!storage || !showId) {
      return null;
    }

    return parseProgress(storage.getItem(buildKey(showId)));
  },

  saveProgress(showId: string, positionSeconds: number, durationSeconds?: number): void {
    const storage = getStorage();
    if (!storage || !showId) {
      return;
    }

    const sanitizedPosition = Math.max(0, Math.floor(positionSeconds));
    if (Number.isNaN(sanitizedPosition)) {
      return;
    }

    const entry: ListeningProgress = {
      positionSeconds: sanitizedPosition,
      durationSeconds:
        typeof durationSeconds === 'number' && Number.isFinite(durationSeconds)
          ? Math.max(0, Math.floor(durationSeconds))
          : undefined,
      updatedAt: new Date().toISOString()
    };

    storage.setItem(buildKey(showId), JSON.stringify(entry));
    enforceStorageLimit(storage);
  },

  clearProgress(showId: string): void {
    const storage = getStorage();
    if (!storage || !showId) {
      return;
    }

    storage.removeItem(buildKey(showId));
  },

  pruneIfComplete(showId: string, positionSeconds: number, durationSeconds?: number): void {
    if (!showId) {
      return;
    }

    if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds)) {
      return;
    }

    if (durationSeconds <= 0) {
      this.clearProgress(showId);
      return;
    }

    if (positionSeconds >= durationSeconds - CLEANUP_THRESHOLD_SECONDS) {
      this.clearProgress(showId);
    }
  },

  clearAll(): void {
    const storage = getStorage();
    if (!storage) {
      return;
    }

    const keysToRemove: string[] = [];

    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => storage.removeItem(key));
  }
} as const;

export default listeningProgressService;
